import { ChartBarIcon } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { day } from "../format.ts";
import { KIT_LABELS, STATUS_LABELS } from "../labels.ts";
import { Chip, StatusIcon } from "./status.tsx";
import { statusMeta, type StatusKind, type Tone } from "./status-kinds.ts";
import { position, type Range, ticks, windowBar } from "./timeline.ts";

// Measurement (docs/design-system.md §0.8, §5.3): a missing number is "–" and
// a day without an observation is a gap, never drawn as zero.

const clamp = (value: number) => Math.min(1, Math.max(0, value));

/** A metric's name with its type icon, shared by lists and properties (§0.8). */
export function MetricChip({ children }: { children: ReactNode }) {
  return <Chip icon={<ChartBarIcon size={12} aria-hidden />}>{children}</Chip>;
}

/** A thin bar: what is used, and what a plan asks for on top of it. */
export function Progress({ value, pending = 0, label, large }: { value: number; pending?: number; label: string; large?: boolean }) {
  const used = clamp(value);
  const asked = Math.min(clamp(pending), 1 - used);
  return <span className={`z-progress${large ? " is-large" : ""}`} role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(used * 100)}>
    <span className="z-progress-used" style={{ width: `${used * 100}%` }} />
    {asked > 0 && <span className="z-progress-pending" style={{ width: `${asked * 100}%` }} />}
  </span>;
}

/** Weekly capacity in minutes. Without a total it says so and shows no bar. */
export function CapacityMeter({ used, requested = 0, total, label = KIT_LABELS.capacity }: { used: number; requested?: number; total: number | null; label?: string }) {
  if (!total) return <div className="z-capacity"><div className="z-capacity-head"><span className="z-muted">{label}</span><span className="z-muted">{KIT_LABELS.notSet}</span></div></div>;
  return <div className="z-capacity">
    <div className="z-capacity-head"><span className="z-muted">{label}</span><span>{KIT_LABELS.capacityOf(used + requested, total)}</span></div>
    <Progress value={used / total} pending={requested / total} label={label} />
    {requested > 0 && <div className="z-capacity-note">{KIT_LABELS.capacityParts(used, requested, Math.max(0, total - used - requested))}</div>}
  </div>;
}

export interface WindowRow {
  label: string;
  sub?: string;
  status: StatusKind;
  progress?: number | null;
  /** Local start date of the window, or null when it has not started. */
  start: string | null;
  days: number | null;
  /** A confirmed result at a date, drawn as a dot in its hue. */
  mark?: { date: string; kind: StatusKind } | null;
  /** Shown instead of a bar, such as "Not started". */
  note?: string;
  /** Where the row's name leads, such as the campaign's page. */
  to?: string;
}

/**
 * Observation windows on a shared date axis in the product's time zone: the
 * part behind today in the state's colour, the rest in its soft colour, and
 * today as a line. Text sits outside the bars; each row reads as one sentence.
 */
export function WindowTimeline({ range, today, rows, tickCount = 5 }: { range: Range; today: string; rows: WindowRow[]; tickCount?: number }) {
  const now = position(range, today);
  return <div className="z-timeline">
    <div className="z-timeline-axis" aria-hidden>
      <span />
      <span className="z-timeline-ticks">{ticks(range, tickCount).map((tick) => <span key={tick} style={{ left: `${position(range, tick) ?? 0}%` }}>{day(tick)}</span>)}</span>
    </div>
    <ol className="z-timeline-rows">
      {rows.map((row, index) => {
        const bar = windowBar(range, row.start, row.days, today);
        const tone: Tone = statusMeta(row.status).tone;
        const end = row.start && row.days ? new Date(Date.parse(`${row.start}T00:00:00Z`) + row.days * 86_400_000).toISOString().slice(0, 10) : null;
        const mark = row.mark ? position(range, row.mark.date) : null;
        return <li key={`${row.label}-${index}`} className="z-timeline-row">
          <span className="z-timeline-label"><StatusIcon kind={row.status} progress={row.progress} label="" /><span>{row.to ? <Link to={row.to} className="z-timeline-name z-link">{row.label}</Link> : <span className="z-timeline-name">{row.label}</span>}{row.sub && <span className="z-timeline-sub">{row.sub}</span>}</span></span>
          <span className="z-timeline-track" aria-hidden>
            {now !== null && <span className="z-timeline-today" style={{ left: `${now}%` }} />}
            {bar ? <span className="z-timeline-bar" data-tone={tone} style={{ left: `${bar.left}%`, width: `${bar.width}%` }}><span style={{ width: `${bar.elapsed}%` }} /></span>
              : <span className="z-timeline-none">{row.note ?? STATUS_LABELS[row.status]}</span>}
            {mark !== null && row.mark && <span className="z-timeline-mark" data-tone={statusMeta(row.mark.kind).tone} style={{ left: `${mark}%` }} />}
          </span>
          <span className="z-sr">{[STATUS_LABELS[row.status], row.start ? day(row.start) : row.note, end ? KIT_LABELS.matures(day(end)) : null].filter(Boolean).join(", ")}</span>
        </li>;
      })}
    </ol>
  </div>;
}

/** A small trend line. Null values are gaps in the line, not zeros. */
export function Sparkline({ values, width = 64, height = 20, label }: { values: (number | null)[]; width?: number; height?: number; label: string }) {
  const present = values.filter((v): v is number => v !== null);
  // Net changes can be negative: the lowest point sets the baseline then.
  const max = Math.max(1, ...present), min = Math.min(0, ...present);
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const segments: string[] = [];
  let current = "";
  values.forEach((v, i) => {
    if (v === null) { if (current) segments.push(current); current = ""; return; }
    const point = `${(i * step).toFixed(1)},${(height - 2 - ((v - min) / (max - min)) * (height - 4)).toFixed(1)}`;
    current += current ? ` L${point}` : `M${point}`;
  });
  if (current) segments.push(current);
  return <svg className="z-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
    {segments.map((d) => <path key={d} d={d} />)}
  </svg>;
}

export type MiniBar = { value: number | null; state: "full" | "partial" | "missing" | "pending"; label: string };

/**
 * A metric card's periods as small bars (§0.8, 2026-10-30), in the full
 * chart's vocabulary: a period under way or with days missing is striped, a
 * period without an observed day is a dashed stub, one without a complete day
 * yet a dotted stub. An observed zero is a flat mark on the baseline.
 */
export function MiniBars({ bars, label }: { bars: MiniBar[]; label: string }) {
  const max = Math.max(0, ...bars.map((b) => b.value ?? 0));
  return <span className={`z-minibars${bars.length > 31 ? " is-dense" : ""}`} role="img" aria-label={label}>
    {bars.map((b, i) => <span key={i} className="z-minibar" data-state={b.state} title={b.label}
      style={b.value === null ? undefined : { height: max > 0 ? `${(Math.max(b.value, 0) / max) * 100}%` : 0 }} />)}
  </span>;
}

/** One metric: its value ("–" when missing), the change and a trend; `tag` marks a primary or guardrail metric on its own line, so the name never gives way to it. */
export function MetricCard({ label, tag, value, delta, deltaTone = "gray", sub, trend, trendLabel, chart, unit, unitNote, onOpen, actions }: {
  label: string; tag?: ReactNode; value: string | null; delta?: string | null; deltaTone?: Tone; sub?: ReactNode; trend?: (number | null)[]; trendLabel?: string;
  /** Drawn instead of `trend`, such as `MiniBars`. */
  chart?: ReactNode;
  unit?: string; unitNote?: string; onOpen?: () => void; actions?: ReactNode;
}) {
  const content = <>
    <span className="z-metric-value">{value ?? <span className="z-muted">–</span>}{unit && <span className="z-metric-unit" title={unitNote}>{unit}</span>}{delta && <span className="z-metric-delta" data-tone={deltaTone}>{delta}</span>}</span>
    {chart ?? (trend && trend.filter(v => v !== null).length > 1 && <Sparkline values={trend} width={160} height={28} label={trendLabel ?? label} />)}
  </>;
  return <div className="z-metric">
    <div className="z-metric-head">{onOpen ? <button type="button" className="z-metric-title-button" onClick={onOpen}>{label}</button> : <span className="z-metric-label">{label}</span>}{actions}</div>
    {tag && <div className="z-metric-tags">{tag}</div>}
    {onOpen ? <button type="button" className="z-metric-open" aria-label={label} onClick={onOpen}>{content}</button> : content}
    {sub && <div className="z-metric-sub">{sub}</div>}
  </div>;
}
